import axios from 'axios';
import { mutate } from 'swr';

import { filterEmptyStrings } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';

export interface IContactAddress {
  isCurrent: boolean;
  includeInDti: boolean;
  monthsAtAddress: number;
  streetLine1: string;
  streetLine2: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  county: string;
  years: number;
  months: number;
}

export interface IContactEmployment {
  monthsAtJob: number;
  jobTitle: string;
  employmentType: string;
  companyName: string;
  monthlyIncomeAmount: number;
  isCurrent: boolean;
  isPrimary: boolean;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  county: string;
}

export interface IContactOtherIncome {
  monthlyAmount: number;
  incomeType: string;
  isCurrent: boolean;
  isPrimary: boolean;
  monthsAtJob: number;
  startDate: string;
  years: number;
  months: number;
}

export interface IContactReference {
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  county: string;
  relationshipType: string;
}

export interface IAsset {
  assetType?: string;
  vin?: string;
  year?: number;
  make?: string;
  model?: string;
  kbbTrim?: string;
  actualNadaTradeIn?: number;
  mileage?: string;
  selectedTradeInValue?: number;
  assetCondition?: string;
}

export interface ICreateContact {
  firstName: string;
  middleInitial: string;
  lastName: string;
  suffix: string;
  maidenName: string;
  nickName: string;
  ssn: string;
  dateOfBirth: string;
  homePhone: string;
  workPhone: string;
  workPhoneExtension: string;
  cellPhone: string;
  email: string;
  militaryStatus: string;
  maritalStatus: string;
  driverLicenseNumber: string;
  driverLicenseExpireDate: string;
  driverLicenseIssuesOnDate: string;
  driverLicenseState: string;
  secondarySource: string;
  currentAddress: IContactAddress;
  previousAddress: IContactAddress;
  currentEmployer: IContactOtherIncome;
  previousEmployer: IContactOtherIncome;
  asset: IAsset;
}

export interface IContactPhones {
  homePhone?: string;
  workPhone?: string;
  cellPhone?: string;
}

export interface ILeadSource {
  id: string;
  name: string;
  description: string;
  isSecondary: boolean;
  allowSecondary: boolean;
  isAutoResponseEnabled: boolean;
  autoResponsePostUrl: string;
  autoResponseVersion: number;
  enableCamRef: boolean;
  isAutoDeadEnabled: boolean;
  isAutoDelayedResponseEnabled: boolean;
  autoDeadDelayedResponse: number;
  autoDelayedResponseOfferCallbackBaseUrl?: unknown;
}

export interface ICreateContactFormProps {
  leadSources: ILeadSource[];
  leadSourcesError: Error;
  isLoading: boolean;
}

const BASE_URL = `/${SERVICES.CONTACT}/contacts`;

const getAddress = (data: ICreateContact) => {
  const arr: IContactAddress[] = [];
  const shouldIncludeCurrent = Object.values(data.currentAddress).find((value: string) => value && value !== '');
  shouldIncludeCurrent && arr.push({ ...data.currentAddress, isCurrent: true });

  const shouldIncludePrevious = Object.values(data.previousAddress).find((value: string) => value && value !== '');
  shouldIncludePrevious && arr.push({ ...data.previousAddress, isCurrent: false });
  return arr;
};

const getEmployments = (data: ICreateContact) => {
  const arr: IContactOtherIncome[] = [];
  const shouldIncludeCurrent = Object.values(data.currentEmployer).find((value: string) => value && value !== '');
  shouldIncludeCurrent && arr.push({ ...data.currentEmployer, isCurrent: true, isPrimary: true, });

  const shouldIncludePrevious = Object.values(data.previousEmployer).find((value: string) => value && value !== '');
  shouldIncludePrevious && arr.push({ ...data.previousEmployer, isCurrent: false, isPrimary:false, });
  return arr;
};

export const requestCreateContact = (contact: ICreateContact) => {
  return mutate(BASE_URL, async() => {
    try {
      const requestModel = filterEmptyStrings({
        firstName: contact.firstName,
        middleInitial: contact.middleInitial,
        lastName: contact.lastName,
        suffix: contact.suffix,
        maidenName: contact.maidenName,
        nickName: contact.nickName,
        ssn: contact.ssn,
        dateOfBirth: contact.dateOfBirth,
        homePhone: contact.homePhone,
        workPhone: contact.workPhone,
        workPhoneExtension: contact.workPhoneExtension ? '' + contact.workPhoneExtension : null,
        cellPhone: contact.cellPhone,
        email: contact.email,
        militaryStatus: contact.militaryStatus,
        maritalStatus: contact.maritalStatus,
        driverLicenseNumber: contact.driverLicenseNumber,
        driverLicenseExpireDate: contact.driverLicenseExpireDate,
        driverLicenseIssuesOnDate: contact.driverLicenseIssuesOnDate,
        driverLicenseState: contact.driverLicenseState,
        secondarySource: contact.secondarySource,
        addresses: getAddress(contact) || [],
        employments: getEmployments(contact) || [],
        asset: contact.asset,
      });
      const response = await axios.post(BASE_URL, requestModel);
      Notistack.successMessage('Contact - Created Successfully');
      return response;
    } catch (err) {
      const error = err as { response: { data: { message: string } } };
      const fError = err as { response: { data: { errors: { value: string }[] } } };
      const message =
        fError?.response?.data?.errors?.[0]?.value || error?.response?.data?.message?.match(/Message\s(.*)/);
      Notistack.generalError(`${message}`);
    }
  });
};
